import './ImportPromptsDialog.scss';
import React, { useRef, useState } from 'react';
import SimpleBar from 'simplebar-react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Papa from 'papaparse';
import { importPrompts } from '../../services/monitor/prompt/ImportPromptsService';
import { useAccount } from '../../contexts/account/AccountContexts';
import { promptFitRejection } from '../../helpers/prompt/promptFit';
import { isAdminOrOwner } from '../../utils/utils';

const CSV_PROMPT_LIMIT = 300;

/**
 * Plik importu bywa zapisany w Windows-1250 — tak Excel na polskim Windowsie zapisuje zwykłe „CSV".
 * `ą` to tam JEDEN bajt (0xB9), w UTF-8 niedozwolony na początku znaku, więc odczyt pliku jako UTF-8
 * zamieniał każdą polską literę w „�" i w takiej postaci pytanie szło do bazy — bezpowrotnie, bo
 * „�" nie niesie oryginalnego bajtu.
 *
 * `fatal: true` jest tu sednem: bez niego dekoder sam podstawia „�" i nie ma jak zauważyć, że plik
 * nie jest UTF-8. Tekst w CP1250 praktycznie nigdy nie jest poprawnym UTF-8, więc wyjątek to pewny
 * sygnał, a nie zgadywanie.
 */
const decodeCsv = (buffer: ArrayBuffer): string => {
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(buffer);
  } catch {
    return new TextDecoder('windows-1250').decode(buffer);
  }
};

interface ImportPromptsDialogProps {
  visible: boolean;
  onHide: () => void;
  projectId: number;
  availableSlots: number;
  onSuccess: () => void;
}

const ImportPromptsDialog: React.FC<ImportPromptsDialogProps> = ({
  visible,
  onHide,
  projectId,
  availableSlots,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { accountResponse, projectResponse } = useAccount();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [parsedPrompts, setParsedPrompts] = useState<string[]>([]);
  const [selectedIndexes, setSelectedIndexes] = useState<number[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [importedCount, setImportedCount] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const isOverLimit = parsedPrompts.length > availableSlots;

  const { mutate: runImport, isPending } = useMutation({
    mutationFn: ({ projectId, prompts }: { projectId: number; prompts: string[] }) =>
      importPrompts(projectId, { prompts }),
    onSuccess: (_, variables) => {
      setImportedCount(variables.prompts.length);
      onSuccess();
      handleClose();
    },
    onError: (error) => {
      // The allocation rule's refusal (nothing imported) is named; anything else reads as a bad file.
      const rejection = promptFitRejection(error);
      const name = projectResponse?.find((project) => project.id === projectId)?.projectLabel ?? '';
      setError(
        rejection?.kind === 'ALLOCATION_EXCEEDED'
          ? t('allocationExceeded', { name, used: rejection.used, allocation: rejection.allocation })
          : rejection?.kind === 'PROJECT_PAUSED'
            ? // Only OWNER / ADMIN can open the hub; a USER is told to ask one.
              t(
                accountResponse && isAdminOrOwner(accountResponse.role)
                  ? 'projectPausedInHub'
                  : 'projectPausedInHubAskAdmin',
                { name }
              )
            : t('csvImportErrorInvalidFormat')
      );
    },
  });

  const processFile = (file: File) => {
    setError(null);
    setImportedCount(null);
    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = decodeCsv(event.target?.result as ArrayBuffer).replace(/^\uFEFF/, '');
      // papaparse auto-detects the delimiter (,/;/tab) and honours quotes, so a comma
      // inside a question no longer truncates the prompt
      const { data, meta } = Papa.parse<string[]>(text, { skipEmptyLines: true });
      const hasHeader = !!data[0]?.[0]?.toLowerCase().includes('prompt');
      const dataRows = hasHeader ? data.slice(1) : data;
      // single-column header => the file has no other columns, so an unquoted comma
      // inside a question must not be read as a column break
      const singleColumn = hasHeader && data[0].length === 1;
      const prompts = dataRows
        .map((row) => (singleColumn ? row.join(meta.delimiter) : row[0] ?? '').trim())
        .filter(Boolean);

      if (prompts.length === 0) {
        setError(t('csvImportErrorInvalidFormat'));
        setParsedPrompts([]);
        setSelectedIndexes([]);
        return;
      }

      if (prompts.length > CSV_PROMPT_LIMIT) {
        setError(t('csvImportErrorLimitExceeded'));
        setParsedPrompts([]);
        setSelectedIndexes([]);
        return;
      }

      setParsedPrompts(prompts);
      setSelectedIndexes(prompts.slice(0, availableSlots).map((_, i) => i));
    };
    reader.readAsArrayBuffer(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    processFile(file);
  };

  const handleDragEnter = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragOver = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setError(t('csvImportErrorInvalidFormat'));
      return;
    }
    processFile(file);
  };

  const togglePrompt = (index: number) => {
    setSelectedIndexes((prev) => {
      if (prev.includes(index)) {
        return prev.filter((i) => i !== index);
      }
      if (prev.length >= availableSlots) return prev;
      return [...prev, index];
    });
  };

  const handleImport = () => {
    if (selectedIndexes.length === 0) return;
    const prompts = [...selectedIndexes].sort((a, b) => a - b).map((i) => parsedPrompts[i]);
    runImport({ projectId, prompts });
  };

  const handleClose = () => {
    setParsedPrompts([]);
    setSelectedIndexes([]);
    setError(null);
    setFileName(null);
    setImportedCount(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    onHide();
  };

  return (
    <Dialog
      visible={visible}
      onHide={handleClose}
      header={t('importFromCsv')}
      className="dialog-box import-prompts-dialog"
      style={{ width: '500px' }}
    >
      <div className="import-prompts-dialog__content">
        <p className="import-prompts-dialog__desc">
          {t('csvImportDialogDesc')}
        </p>

        <ul className="import-prompts-dialog__format-list">
          <li>{t('csvImportFormatOnePerRow')}</li>
          <li>{t('csvImportFormatFirstColumn')}</li>
          <li>{t('csvImportFormatPlainText')}</li>
        </ul>

        <a
          href="/prompt-template.csv"
          download="prompt-template.csv"
          className="import-prompts-dialog__template-link"
        >
          <span className="ico--download ico--xs" />
          {t('csvDownloadTemplate')}
        </a>

        {/* Hidden real input */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          onChange={handleFileChange}
          className="import-prompts-dialog__file-input--hidden"
          id="import-csv-input"
        />
        {/* Custom styled trigger */}
        <label
          htmlFor="import-csv-input"
          className={`import-prompts-dialog__upload-zone${isDragging ? ' import-prompts-dialog__upload-zone--dragging' : ''}`}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <span className="ico--download ico--lg import-prompts-dialog__upload-icon" />
          <span className="import-prompts-dialog__upload-text">
            {isPending
              ? t('csvImportUploading')
              : fileName
              ? fileName
              : t('csvImportClickToSelect')}
          </span>
        </label>

        {error && <div className="import-prompts-dialog__error">{error}</div>}

        {importedCount !== null && !error && (
          <div className="import-prompts-dialog__success">
            {t('csvImportSuccess', { count: importedCount })}
          </div>
        )}

        {parsedPrompts.length > 0 && (
          <>
            {isOverLimit && (
              <div className="import-prompts-dialog__limit-warning">
                {t('csvImportSlotWarning', { slots: availableSlots })}
                {' '}
                {t('csvImportSelected', { selected: selectedIndexes.length, slots: availableSlots })}
              </div>
            )}

            <SimpleBar style={{ maxHeight: '280px' }}>
              <ul className="import-prompts-dialog__list">
              {parsedPrompts.map((prompt, i) => {
                const isSelected = selectedIndexes.includes(i);
                const isDisabled = isOverLimit && !isSelected && selectedIndexes.length >= availableSlots;
                return (
                  <li
                    key={i}
                    className={`import-prompts-dialog__item${isDisabled ? ' import-prompts-dialog__item--disabled' : ''}`}
                  >
                    {isOverLimit ? (
                      <Checkbox
                        checked={isSelected}
                        disabled={isDisabled}
                        onChange={() => togglePrompt(i)}
                      />
                    ) : (
                      <span className="ico--tick ico--xs import-prompts-dialog__check" />
                    )}
                    <span className="import-prompts-dialog__prompt">{prompt}</span>
                  </li>
                );
              })}
              </ul>
            </SimpleBar>

            <div className="import-prompts-dialog__footer">
              <Button
                className="btn btn--sm"
                label={t('csvImportButton', { count: selectedIndexes.length })}
                onClick={handleImport}
                disabled={selectedIndexes.length === 0 || isPending}
              />
              <Button className="btn btn--sm btn--secondary" label={t('cancel')} onClick={handleClose} />
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
};

export default ImportPromptsDialog;
